import React, { FC, memo, useContext, useMemo, useState } from "react"
import { Alert, Button, Form, Input, Space, Spin, message } from "antd"
import { Formik } from "formik"
import * as Yup from "yup"
import { useHistory, useLocation, useParams } from "react-router"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import LoginContainer from "../../views/LoginContainer/LoginContainer"
import LayoutForm from "../../views/LayoutForm/LayoutForm"
import { useCompleteBookerLinkRegistrationMutation } from "../../generated/graphql"
import { MainContext } from "../../layouts/Main/MainLayout"
import { URL_LOGIN, URL_REGISTER } from "../../models/url"
import { safeStorage } from "../../helpers/safeStorage"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import { clearPersistedLoginFromUrl, resolveLoginFromUrl } from "./loginFromUrl"
import { grey } from "@ant-design/colors"
import { margin } from "../../styles/layout"
import { LocationState } from "../../types"
import { getGraphQLErrorMessage } from "../../helpers/sentry"

type ActivateAccountPanelProps = {
    setToken: (token: string) => void
}

type ActivateAccountValues = {
    password: string
    passwordConfirmation: string
}

/**
 * Aktivierungsseite für den Booker-Verknüpfungs-Flow (sc-3358): Der Nutzer kommt
 * per Bestätigungs-Mail auf /activate-account/:token, wählt hier sein Passwort und
 * wird nach erfolgreicher Aktivierung direkt eingeloggt (gleiches Login-Ritual wie
 * CompletePasswordLessFlowPanel).
 */
const ActivateAccountPanel: FC<ActivateAccountPanelProps> = ({ setToken }) => {
    const { token } = useParams<{ token: string }>()
    const history = useHistory()
    const location = useLocation<LocationState>()
    const { userAuthDate } = useContext(MainContext)
    const { t, i18n } = useTranslation()
    // Mail-Link öffnet eine neue Navigation ohne Location-State → Fallback auf die
    // persistierte fromUrl bzw. URL_BASE (siehe loginFromUrl.ts).
    const fromUrl = resolveLoginFromUrl(location.state?.fromUrl)

    const [errorKey, setErrorKey] = useState<"invalidToken" | "unknown" | undefined>()
    const [completeBookerLinkRegistration, { loading }] = useCompleteBookerLinkRegistrationMutation()

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                password: Yup.string()
                    .min(8, t("registration.help.passwordLength", "Passwort muss mindestens 8 Zeichen lang sein"))
                    .required(t("registration.help.password", "Passwort ist ein Pflichtfeld")),
                passwordConfirmation: Yup.string()
                    .required(t("registration.help.confirmPassword", "Bitte wiederhole das Passwort"))
                    .oneOf([Yup.ref("password"), null], t("registration.help.confirmPasswordMatch", "Die Passwörter müssen überinstimmen")),
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, i18n.language]
    )

    const handleActivate = async (values: ActivateAccountValues) => {
        // Bereits eingeloggt → nur weiterleiten (Mutation würde am Replay-Guard scheitern)
        if (safeStorage.getItem("loggedInUser")) {
            clearPersistedLoginFromUrl()
            history.push(fromUrl)
            return
        }

        setErrorKey(undefined)

        try {
            const { data } = await completeBookerLinkRegistration({ variables: { token, password: values.password } })
            const result = data?.completeBookerLinkRegistration

            if (result?.accessToken && result.refreshToken && result.booker && result.id) {
                setToken(result.accessToken)
                safeStorage.setItem("accessToken", result.accessToken)
                safeStorage.setItem("refreshToken", result.refreshToken)
                safeStorage.setItem("booker", JSON.stringify(result.booker))
                safeStorage.setItem("loggedInUser", result.id)
                // App.tsx hört auf dieses Event und setzt isLoggedIn — ohne Dispatch
                // bliebe der Header im ausgeloggten Zustand.
                window.dispatchEvent(new Event("localStorageChange"))
                if (userAuthDate) {
                    userAuthDate(new Date())
                }
                message.success(t("activateAccount.success", "Dein Zugang wurde aktiviert — willkommen!"), 3)
                clearPersistedLoginFromUrl()
                history.push(fromUrl)
            } else {
                setErrorKey("unknown")
            }
        } catch (error: any) {
            const rawErrorMessage = getGraphQLErrorMessage(error) || JSON.stringify(error)
            if (rawErrorMessage.includes("ungültig oder abgelaufen")) {
                setErrorKey("invalidToken")
            } else {
                Sentry.captureException(error)
                setErrorKey("unknown")
            }
        }
    }

    const renderError = () => {
        if (errorKey === "invalidToken") {
            return (
                <Alert
                    type='error'
                    style={{ textAlign: "center" }}
                    message={t("activateAccount.invalidToken.title", "Link ungültig oder abgelaufen")}
                    description={
                        <Space direction='vertical' style={{ paddingTop: 12, paddingBottom: 4 }}>
                            <p>
                                {t(
                                    "activateAccount.invalidToken.msg",
                                    "Dieser Aktivierungslink ist nicht mehr gültig (er ist 24 Stunden nutzbar und funktioniert nur einmal). Starte die Registrierung einfach erneut, um einen neuen Link zu erhalten."
                                )}
                            </p>
                            <Button type='primary' ghost block>
                                <Link to={URL_REGISTER}>{t("activateAccount.invalidToken.toRegistration", "Zur Registrierung")}</Link>
                            </Button>
                        </Space>
                    }
                />
            )
        }

        if (errorKey === "unknown") {
            return (
                <Alert
                    type='error'
                    style={{ textAlign: "center" }}
                    message={t("activateAccount.unknownError.title", "Aktivierung fehlgeschlagen")}
                    description={t("activateAccount.unknownError.msg", "Es gab einen unerwarteten Fehler. Bitte versuche es in ein paar Minuten erneut.")}
                />
            )
        }

        return null
    }

    const renderContent = () => {
        if (loading) {
            return (
                <Space direction='vertical' size='large'>
                    <h3 style={{ color: grey[3] }}>{t("activateAccount.loading.title", "Einen Moment bitte")}</h3>
                    <Spin />
                    <h2 style={{ color: grey[4], marginBottom: margin.std, fontWeight: 600 }}>{t("activateAccount.loading.msg", "Wir aktivieren deinen Zugang...")}</h2>
                </Space>
            )
        }

        return (
            <Formik<ActivateAccountValues> initialValues={{ password: "", passwordConfirmation: "" }} validationSchema={validationSchema} onSubmit={handleActivate}>
                {(formikProps) => {
                    const { handleSubmit, errors, touched } = formikProps
                    const { handleTextInputChange } = createInputHelpers<ActivateAccountValues>(formikProps)

                    const fieldProps = (field: keyof ActivateAccountValues) => ({
                        validateStatus: (touched[field] && errors[field] ? "error" : undefined) as "error" | undefined,
                        help: touched[field] ? errors[field] : undefined,
                    })

                    return (
                        <LayoutForm onSubmitCapture={handleSubmit} columns={1}>
                            <p style={{ textAlign: "center" }}>
                                {t(
                                    "activateAccount.description",
                                    "Schön, dass du da bist! Lege ein Passwort fest, um deinen bestehenden Kundendatensatz mit deinem neuen Online-Zugang zu verknüpfen."
                                )}
                            </p>
                            {renderError()}
                            <Form.Item label={t("activateAccount.password", "Passwort")} {...fieldProps("password")}>
                                <Input.Password
                                    data-testid='activate-account-password-input'
                                    value={formikProps.values.password}
                                    onChange={handleTextInputChange("password")}
                                    onBlur={() => formikProps.setFieldTouched("password")}
                                />
                            </Form.Item>
                            <Form.Item label={t("activateAccount.passwordConfirmation", "Passwort wiederholen")} {...fieldProps("passwordConfirmation")}>
                                <Input.Password
                                    data-testid='activate-account-password-confirm-input'
                                    value={formikProps.values.passwordConfirmation}
                                    onChange={handleTextInputChange("passwordConfirmation")}
                                    onBlur={() => formikProps.setFieldTouched("passwordConfirmation")}
                                />
                            </Form.Item>
                            <Button htmlType='submit' type='primary' block data-testid='activate-account-submit' disabled={loading}>
                                {t("activateAccount.submit", "Zugang aktivieren")}
                            </Button>
                        </LayoutForm>
                    )
                }}
            </Formik>
        )
    }

    return <LoginContainer title={t("activateAccount.title", "Kunden-Zugang aktivieren")} size='xs' style={{ paddingBottom: margin.std }} content={renderContent()} backToUrl={URL_LOGIN} />
}

export default memo(ActivateAccountPanel)
